import InfoIcon from "@mui/icons-material/Info";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import { Box, Grid, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import { ContactPreviewButton } from "../../components/buttons/ContactPreviewButton";
import { ContactEditButton } from "../../components/buttons/ContactEditButton";
import { TextFilter } from "../../components/common/TextFilter";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import i18n from "../../i18n";
import { useContactSubjects, useContacts, useContactChannelsReport } from "../../services/contactsService";
import { useLeadsSourceChannels } from "../../services/leadsService";
import { useInvestments } from "../../services/productsService";
import TableService from "../../services/tableService";
import { ContactsDialog } from "../PersonRepositoryPage/ContactsDialog";
import { EditConnectedPersonDialog } from "../PersonRepositoryPage/EditConnectedPersonDialog";
import { CONTACTS_TABLE_NAME } from "./ContactPageTableConfig";
import { SELECT_FIELD_TYPE, DATE_RANGE_FIELD_TYPE } from "../../helpers/constants";
import DateService from "../../services/dateService";

export function ContactPage(props) {
    const { pageName, isContactReportChannelPage } = props;
    const { t } = useTranslation();
    const [hasEditPermission] = [true]; // Implement permission check if needed

    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);

    const { openDialog: openContactsDialog } = useDialogBetter();
    const { openDialog: openEditConnectedPersonDialog } = useDialogBetter();
    const { data: subjects } = useContactSubjects({
        select: (data) =>
            data.map((item) => ({
                value: item.contsubj_name_eng,
                label: t(`contacts.subjects.${item.contsubj_name_eng}`),
            })),
    });

    const { data: sources } = useLeadsSourceChannels({
        enabled: isContactReportChannelPage,
        select: (data) =>
            data.results.map((channel) => ({
                value: channel.channel_name,
                label: t(`lead.sources.${channel.channel_name_eng}`),
        })),
    });

    const { data: investments } = useInvestments({
        select: (data) =>
            data.results.map((investment) => ({
                value: investment.id,
                label: investment.investment_name_sales,
            })),
    });

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const fetchContanctFn = isContactReportChannelPage ? useContactChannelsReport : useContacts

    const { data: contactsData, refetch, isLoading } = fetchContanctFn(searchParams, {
        select: (data) => data,
    });

    useEffect(() => {
        if (contactsData?.results) {
            setDataLocal(contactsData.results);
            setRecordsCount(contactsData.count);
        }
    }, [contactsData, dataPage, dataPageSize]);

    const contactsTableFieldsConfig = useMemo(() => {
        let tableConfig = [
            {
                name: "passportBtn",
                getValueCallback: (rowData) => {
                    if (rowData.person.customer_id === null) {
                        return (
                            <IconButton
                                color="primary"
                                onClick={() => {
                                    openEditConnectedPersonDialog({
                                        Component: EditConnectedPersonDialog,
                                        componentProps: {
                                            personId: rowData.person.id,
                                        },
                                    });
                                }}
                            >
                                <InfoIcon />
                            </IconButton>
                        );
                    }
                    return (
                        <ClientPassportButton
                            clientId={rowData.person.customer_id}
                            clientType="customer"
                            color="primary"
                        />
                    );
                },
                // @ts-ignore
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
                headerSx: { whiteSpace: "break-spaces" },
            },
            {
                name: "fname",
                getValueCallback: (rowData) => rowData.person.name,
                label: i18n.t("table.contacts.firstName"),
                filterType: "text",
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
                minWidth: 120,
                width: 120,
                headerSx: { whiteSpace: "break-spaces" },
            },
            {
                name: "lname",
                getValueCallback: (rowData) => rowData.person.surname,
                label: i18n.t("table.contacts.lastName"),
                filterType: "text",
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
                minWidth: 120,
                width: 120,
                headerSx: { whiteSpace: "break-spaces" },
            },
            {
                name: "phone",
                getValueCallback: (rowData) => rowData.person.phone?.phone_number,
                label: i18n.t("table.contacts.phone"),
                filterType: "text",
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                headerSx: { whiteSpace: "break-spaces" },
            },
            {
                name: "email",
                getValueCallback: (rowData) => rowData.person.email?.email_address,
                label: i18n.t("table.contacts.email"),
                filterType: "text",
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                headerSx: { whiteSpace: "break-spaces" },
            },
            {
                name: "way",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => i18n.t(`contacts.waysOfContact.${rowData.way}`),
                getFilterFieldConfigCallback: (filteringData) => ({
                    options: [
                        {
                            value: "Email",
                            label: i18n.t("contacts.waysOfContact.Email"),
                        },
                        {
                            value: "Phone",
                            label: i18n.t("contacts.waysOfContact.Phone"),
                        },
                        {
                            value: "Meeting",
                            label: i18n.t("contacts.waysOfContact.Meeting"),
                        },
                        {
                            value: "Other",
                            label: i18n.t("contacts.waysOfContact.other"),
                        },
                    ],
                    optionKey: "value",
                    optionLabel: "label",
                }),
                label: i18n.t("table.contacts.way"),
                filterType: "select",
                sortable: true,
                fieldConfig: {
                    optionKey: "value",
                    optionLabel: "label",
                    options: [
                        {
                            value: "Email",
                            label: i18n.t("contacts.waysOfContact.Email"),
                        },
                        {
                            value: "Phone",
                            label: i18n.t("contacts.waysOfContact.Phone"),
                        },
                        {
                            value: "Meeting",
                            label: i18n.t("contacts.waysOfContact.Meeting"),
                        },
                        {
                            value: "Other",
                            label: i18n.t("contacts.waysOfContact.other"),
                        },
                    ],
                },
            },
            {
                name: "subject",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => i18n.t(`contacts.subjects.${rowData.subject}`),
                label: i18n.t("table.contacts.subject"),
                filterType: "select",
                sortable: true,
                getFilterFieldConfigCallback: (filteringData) => ({
                    options: subjects,
                    optionKey: "value",
                    optionLabel: "label",
                }),
                fieldConfig: {
                    optionKey: "value",
                    optionLabel: "label",
                    options: subjects,
                },
            },
            {
                name: "date",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => rowData.date,
                label: i18n.t("table.contacts.date"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: true,
            },
            {
                name: "note",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => rowData.note,
                label: i18n.t("table.contacts.note"),
                filterType: "text",
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
            },
            {
                name: "seller",
                getValueCallback: (rowData) => rowData.seller?.full_name,
                label: i18n.t("table.contacts.seller"),
                filterType: "text",
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: true,
            },
            {
                name: "contact",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => (
                    <IconButton
                        color="primary"
                        onClick={() => {
                            openContactsDialog({
                                Component: ContactsDialog,
                                componentProps: {
                                    personId: rowData.person.id,
                                    isPreview: false,
                                },
                            });
                        }}
                    >
                        <LocalPhoneIcon />
                    </IconButton>
                ),
                label: i18n.t("table.contacts.newContact"),
                // @ts-ignore
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
            },
            {
                name: "channel_name",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => rowData.channel_name,
                label: i18n.t("table.contacts.channelName"),
                filterType: SELECT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    options: sources,
                    optionKey: "value",
                    optionLabel: "label",
                }),
                sortable: false,
                fieldConfig: {
                    optionKey: "value",
                    optionLabel: "label",
                    options: sources,
                },
            },
            {
                name: "source_lead_investment",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => rowData.source_lead_investment,
                label: i18n.t("table.contacts.investment"),
                filterType: SELECT_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    optionKey: "value",
                    optionLabel: "label",
                    options: investments,
                    multiple: false,
                }),
                fieldConfig: {
                    optionKey: "value",
                    optionLabel: "label",
                    options: investments,
                },
            },
            {
                name: "source_lead_date",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) =>
                    DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.source_lead_date),
                label: t("table.contacts.sourceLeadDate"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: true,
            },
            {
                name: "first_lead_date",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) =>
                    DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.first_lead_date),
                label: t("table.contacts.firstLeadDate"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: true,
            },
            {
                name: "last_lead_date",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) =>
                    DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.last_lead_date),
                label: t("table.contacts.lastLeadDate"),
                filterType: DATE_RANGE_FIELD_TYPE,
                getFilterFieldConfigCallback: (filteringData) => ({
                    style: { minWidth: "100px" },
                }),
                sortable: true,
            },
            {
                name: "preview",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => (
                    <ContactPreviewButton
                        personId={rowData.person.id}
                        rowData={{
                            way: rowData.way,
                            subject: rowData.subject,
                            date: rowData.date,
                            note: rowData.note,
                            products: rowData.products,
                            ...(rowData.offer && { offerId: rowData.offer.id }),
                        }}
                    />
                ),
                label: i18n.t("table.contacts.preview"),
                // @ts-ignore
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
            },
            {
                name: "edit",
                headerSx: { whiteSpace: "break-spaces" },
                getValueCallback: (rowData) => (
                    <ContactEditButton
                        personId={rowData.person.id}
                        rowData={{
                            id: rowData.id,
                            way: rowData.way,
                            subject: rowData.subject,
                            date: rowData.date,
                            note: rowData.note,
                            products: rowData.products,
                            ...(rowData.offer && { offerId: rowData.offer.id }),
                        }}
                    />
                ),
                label: i18n.t("table.contacts.edit"),
                // @ts-ignore
                getFilterFieldConfigCallback: (filteringData) => undefined,
                sortable: false,
                excludedFromExport: true,
                allowChangeVisibility: false,
            },
        ];
        const reportPageExludedFields = ["contact", "edit"]
        const contactPageExludedFields = ["channel_name", "source_lead_investment", "source_lead_date", "first_lead_date", "last_lead_date"]

        return (
            isContactReportChannelPage ? 
            tableConfig.filter(config=>!reportPageExludedFields.includes(config.name)) :
            tableConfig.filter(config=>!contactPageExludedFields.includes(config.name))
        ) 

    }, [openContactsDialog]);

    const contactDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            contactsTableFieldsConfig, // @ts-ignore
            hiddenColumnsForTables?.[CONTACTS_TABLE_NAME], // @ts-ignore
            columnsOrdersForTables?.[CONTACTS_TABLE_NAME],
        );
    }, [
        // @ts-ignore
        hiddenColumnsForTables?.[CONTACTS_TABLE_NAME],
        // @ts-ignore
        columnsOrdersForTables?.[CONTACTS_TABLE_NAME],
        contactsTableFieldsConfig,
    ]);

    // @ts-ignore
    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
        // This function can be used for row buttons if needed
    }, []);

    useEffect(() => {
        refetch();
    }, [searchParams, refetch]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                contactsTableFieldsConfig,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[CONTACTS_TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[CONTACTS_TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[CONTACTS_TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[CONTACTS_TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            contactsTableFieldsConfig,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={isContactReportChannelPage ? t("headers.contact_channel_report") : t("headers.contacts")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                rowSpacing={0}
            >
                <Grid item xs={4}>
                        <TextFilter
                            filterKey={"person"}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                            resetPageNumber={resetPageNumber}
                            withPagination
                            placeholder={
                                t("clients.filter.masterFilterPersonPlaceholder") ?? undefined
                            }
                        />
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={{
                            name: CONTACTS_TABLE_NAME,
                            config: contactsTableFieldsConfig,
                        }}
                        filteringData={() => {}}
                        refreshTable={refetch}
                        // @ts-ignore
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={true}
                        showMyFiltersButton={true}
                        showSearchButton={true}
                        itemType={"contact"}
                        style={{ marginBlock: "5px" }}
                        showCreateButton={false}
                        mdGridButton={hasEditPermission ? 2.25 : 3.75}
                        showImportList={false}
                        showRemoveLeadButton={false}
                    />
                </Grid>
                <Grid item xs={12}>
                    {/* TODO: Loader messes up filter inputs because it reloads table on filter change */}
                    <LoaderWrapper showLoader={false}>
                        <FilterTable
                            // @ts-ignore
                            data={data}
                            countRecords={recordsCount}
                            resetPageNumber={resetPageNumber}
                            selectedItemId={selectedRowId}
                            style={{ maxHeight: "60vh" }}
                            headersConfig={TableService.orderColumns(
                                TableService.hideHiddenColumns(
                                    contactsTableFieldsConfig, // @ts-ignore
                                    hiddenColumnsForTables?.[CONTACTS_TABLE_NAME]
                                        ? // @ts-ignore
                                          hiddenColumnsForTables?.[CONTACTS_TABLE_NAME]
                                        : [],
                                ), // @ts-ignore
                                columnsOrdersForTables?.[CONTACTS_TABLE_NAME]
                                    ? // @ts-ignore
                                      columnsOrdersForTables?.[CONTACTS_TABLE_NAME]
                                    : [],
                            )}
                            showCleanFilterIcon={true}
                            showContextMenu={false}
                            showExportToFileButton={true}
                            exportToFileUrl={isContactReportChannelPage ? "sales/contacts/channel_report/export/" : "sales/contacts/export/"}
                            exportToFileSearchParams={searchParams}
                            exportToFileHeaders={contactDataExportHeaders}
                            exportToFileFileName={`${t("contact.exportFileName")}.xlsx`}
                            showDetailsIcon={false}
                            showCheckbox={false}
                            checkedAll={checkedAll}
                            page={dataPage}
                            onPageChange={handleChangeDataPage}
                            rowsPerPage={dataPageSize}
                            onRowsPerPageChange={handleChangeDataPageSize}
                            setDataLocal={setDataLocal}
                            setCheckedAll={setCheckedAll}
                            setSelectedRowId={setSelectedRowId}
                            isLoading={isLoading}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
